import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { CheckCircle2, Clock, Download, BellRing, Loader2, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { qmbAPI } from '../../lib/api';
import { fmtDate } from './qmbConstants';

export const ConfirmationsDialog = ({ open, onOpenChange, post }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(false);

  const load = useCallback(async () => {
    if (!post) return;
    setLoading(true);
    try {
      const res = await qmbAPI.getConfirmations(post.id);
      setData(res.data);
    } catch (e) {
      toast.error('Bestätigungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [post]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const remind = async () => {
    setReminding(true);
    try {
      const res = await qmbAPI.remind(post.id);
      toast.success(res.data?.message || 'Erinnerung gesendet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erinnerung fehlgeschlagen');
    } finally {
      setReminding(false);
    }
  };

  const pct = data && data.total > 0 ? Math.round((data.confirmed_count / data.total) * 100) : 0;

  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="qmb-confirmations-dialog">
        <DialogHeader>
          <DialogTitle>Lesebestätigungen</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="space-y-3"><Skeleton className="h-10" /><Skeleton className="h-40" /></div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm font-medium truncate">{data?.title}</p>
            {!data?.require_confirmation && (
              <p className="text-xs text-muted-foreground">Dieser Beitrag erfordert keine Lesebestätigung – die Liste zeigt nur die Zielgruppe.</p>
            )}
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span data-testid="qmb-conf-progress-label">{data?.confirmed_count} / {data?.total} bestätigt</span>
                <span className="text-muted-foreground">{pct}%</span>
              </div>
              <Progress value={pct} className="h-2" />
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={qmbAPI.exportUrl(post.id)} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="qmb-export-btn"><Download className="h-4 w-4 mr-1" /> Excel-Export</Button>
              </a>
              {data?.require_confirmation && data?.pending_count > 0 && (
                <Button variant="outline" size="sm" onClick={remind} disabled={reminding} data-testid="qmb-remind-btn">
                  {reminding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <BellRing className="h-4 w-4 mr-1" />}
                  {data.pending_count} Offene erinnern
                </Button>
              )}
            </div>
            <ScrollArea className="h-64 rounded border">
              <div className="divide-y">
                {(data?.rows || []).map((r) => (
                  <div key={r.employee_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`qmb-conf-row-${r.employee_id}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{r.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.department || '—'}</p>
                    </div>
                    {!r.has_login ? (
                      <Badge variant="outline" className="text-amber-700 border-amber-300 gap-1 shrink-0">
                        <UserX className="h-3 w-3" /> Kein Login
                      </Badge>
                    ) : r.confirmed ? (
                      <Badge className="bg-emerald-100 text-emerald-700 gap-1 shrink-0">
                        <CheckCircle2 className="h-3 w-3" /> {fmtDate(r.confirmed_at)}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground gap-1 shrink-0">
                        <Clock className="h-3 w-3" /> Offen
                      </Badge>
                    )}
                  </div>
                ))}
                {(data?.rows || []).length === 0 && (
                  <p className="text-sm text-muted-foreground p-4 text-center">Keine Mitarbeiter in der Zielgruppe.</p>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
